import { useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ScrollToTopOnNavigate from '@/components/feature/ScrollToTopOnNavigate';
import { mangaList, categories } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const filteredManga = useMemo(() => {
    let result = mangaList;

    if (searchQuery) {
      result = result.filter(
        (m) =>
          m.title.includes(searchQuery) ||
          m.author.includes(searchQuery) ||
          m.description.includes(searchQuery)
      );
    } else if (activeCategory && activeCategory !== 'all') {
      result = result.filter((m) => m.category === activeCategory);
    }

    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort(
        (a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()
      );
    }

    return result;
  }, [activeCategory, sortBy, searchQuery]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '全部'}漫画 - 吹雪漫画网`,
    description: `吹雪漫画网${currentCategory?.name || '全部'}漫画分类，免费无删减漫画在线阅读，下拉式流畅体验。`,
    url: `https://chuixue.com/category/${activeCategory}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '吹雪漫画网',
      url: 'https://chuixue.com',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/all' },
  ];
  if (currentCategory && currentCategory.slug !== 'all') {
    breadcrumbItems.push({ label: currentCategory.name });
  }

  return (
    <>
      <ScrollToTopOnNavigate />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryJsonLd) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
          <div className="w-full px-4 md:px-6 pb-6">
            <div className="max-w-7xl mx-auto">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                {searchQuery
                  ? `搜索：${searchQuery}`
                  : currentCategory
                    ? `${currentCategory.name}漫画`
                    : '全部漫画'}
              </h1>
              <p className="text-sm text-foreground-500">
                {searchQuery
                  ? `找到 ${filteredManga.length} 部相关漫画`
                  : `共 ${filteredManga.length} 部漫画作品`}
              </p>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        {!searchQuery && (
          <div className="w-full px-4 md:px-6 py-3 border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm w-3.5 h-3.5 flex items-center justify-center`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sort Bar */}
        <div className="w-full px-4 md:px-6 py-3 bg-background-50 border-b border-background-100/70">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
            {[
              { key: 'latest', label: '最新更新' },
              { key: 'popular', label: '最受欢迎' },
              { key: 'rating', label: '评分最高' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key as typeof sortBy)}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === opt.key
                    ? 'bg-secondary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Manga Grid */}
        <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredManga.map((manga) => (
                  <Link
                    key={manga.slug}
                    to={`/article/${manga.slug}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg bg-background-200 aspect-[3/4] mb-2">
                      <img
                        src={manga.cover}
                        alt={manga.title}
                        title={`${manga.title} - 吹雪漫画网`}
                        className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-2 right-2 px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-full">
                        {manga.rating}
                      </div>
                      <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full">
                        {manga.status}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                      {manga.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{manga.author}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">
                      {manga.chapters} 话 · {Math.floor(manga.views / 10000)}万阅读
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </span>
                <p className="text-foreground-600 text-sm">没有找到相关漫画</p>
                <p className="text-foreground-500 text-xs mt-1">试试其他关键词或分类吧</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}